/**
 * Universal Intake Form
 * 
 * Platform Principle 15: "Information Should Enter the Platform Naturally."
 * 
 * One Component. One Experience. Forever.
 * 
 * This component wraps any entity creation/edit form and provides:
 * - AI-powered data entry via Universal AI Toolbar
 * - AI Review Mode with confidence scores and source attribution
 * - Seamless integration with existing forms
 * - Works for ALL entity types: Clients, Employees, Applicants, Referrals,
 *   Assessments, Care Plans, Authorizations, Incidents, Communications, HR Notes
 * 
 * Usage:
 * <UniversalIntakeForm
 *   entityType="client"
 *   onDataExtracted={(fields) => populateForm(fields)}
 *   onSave={(data) => saveClient(data)}
 *   existingData={client}  // For edit mode
 * />
 * 
 * The component sits ON TOP of existing forms, not replacing them.
 * It's a platform layer for data entry assistance.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { UniversalAIToolbar } from './universal-ai-toolbar';
import { AIReviewMode } from './ai-review-mode';
import { Badge } from './badge';
import { toast } from 'sonner';
import { 
  Sparkles, Wand2, X, ChevronRight, FileText, 
  AlertCircle, CheckCircle2, Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

// Entity type configurations
const ENTITY_CONFIG = {
  client: {
    label: 'Client',
    description: 'Patient/client intake with demographics, health info, and contacts',
    endpoint: '/clients',
    icon: '👤',
  },
  employee: {
    label: 'Employee',
    description: 'Caregiver or staff member profile',
    endpoint: '/employees',
    icon: '👥',
  },
  applicant: {
    label: 'Applicant',
    description: 'Job applicant for caregiver position',
    endpoint: '/hiring/applications',
    icon: '📋',
  },
  referral: {
    label: 'Referral',
    description: 'New client referral from hospital, case manager, or agency',
    endpoint: '/referrals',
    icon: '📨',
  },
  assessment: {
    label: 'Assessment',
    description: 'Client assessment including ADLs, IADLs, cognitive status',
    endpoint: '/assessments',
    icon: '📝',
  },
  care_plan: {
    label: 'Care Plan',
    description: 'Authorized services, goals, and caregiver requirements',
    endpoint: '/care-plans',
    icon: '❤️',
  },
  authorization: {
    label: 'Authorization',
    description: 'Service authorization from payer (VA, Medicaid, etc.)',
    endpoint: '/authorizations',
    icon: '✅',
  },
  incident: {
    label: 'Incident',
    description: 'Incident report for falls, medication errors, etc.',
    endpoint: '/incidents',
    icon: '⚠️',
  },
  communication: {
    label: 'Communication',
    description: 'Communication log entry (call, email, meeting)',
    endpoint: '/communications',
    icon: '💬',
  },
  hr_note: {
    label: 'HR Note',
    description: 'HR note for employee (performance, disciplinary, training)',
    endpoint: '/hr-notes',
    icon: '📁',
  },
};

// Modes for the intake form
const INTAKE_MODES = {
  TOOLBAR: 'toolbar',      // Shows AI toolbar for input
  REVIEWING: 'reviewing',  // Shows AI Review Mode
  COMPLETE: 'complete',    // Extraction complete, ready to populate form
};

/**
 * Universal Intake Form Component
 * 
 * Sits on top of any existing form to provide AI-assisted data entry.
 */
export function UniversalIntakeForm({
  entityType,
  onDataExtracted,
  onSave,
  existingData = null,
  isModal = false,
  onClose,
  className,
  showTitle = true,
  compact = false,
}) {
  const [mode, setMode] = useState(INTAKE_MODES.TOOLBAR);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [approvedData, setApprovedData] = useState(null);
  
  const config = ENTITY_CONFIG[entityType] || {
    label: entityType,
    description: 'Entity intake',
    endpoint: `/${entityType}s`,
    icon: '📄',
  };
  
  // Handle extraction from toolbar
  const handleExtract = useCallback(async (extractionData) => {
    setIsExtracting(true);
    
    try {
      let response;
      
      if (extractionData.method === 'text') {
        response = await api.post('/intake/extract/text', {
          text: extractionData.content,
          entity_type: entityType,
          input_method: extractionData.inputMethod || 'natural_language',
          context: existingData,
        });
      } else if (extractionData.method === 'voice') {
        response = await api.post('/intake/extract/voice', {
          audio_base64: extractionData.content,
          audio_format: extractionData.audioFormat || 'audio/webm',
          entity_type: entityType,
          context: existingData,
        });
      } else if (extractionData.method === 'document') {
        const formData = new FormData();
        formData.append('file', extractionData.file);
        formData.append('entity_type', entityType);
        if (existingData) {
          formData.append('context', JSON.stringify(existingData));
        }
        
        response = await api.post('/intake/extract/document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (extractionData.method === 'image') {
        const formData = new FormData();
        formData.append('file', extractionData.file);
        formData.append('entity_type', entityType);
        if (existingData) {
          formData.append('context', JSON.stringify(existingData));
        }
        
        response = await api.post('/intake/extract/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      if (response?.data?.success) {
        setExtractionResult(response.data);
        setMode(INTAKE_MODES.REVIEWING);
        toast.success(`Extracted ${response.data.fields?.length || 0} fields with ${Math.round(response.data.summary?.overall_confidence * 100)}% confidence`);
      } else {
        toast.error(response?.data?.error || 'Extraction failed');
      }
    } catch (err) {
      console.error('Extraction error:', err);
      toast.error(err.response?.data?.detail || 'Extraction failed. Please try again.');
    } finally {
      setIsExtracting(false);
    }
  }, [entityType, existingData]);
  
  // Handle confirmation from AI Review Mode
  const handleConfirm = useCallback(async (confirmData) => {
    setIsConfirming(true);
    
    try {
      // Log the confirmation with corrections for learning
      await api.post('/intake/confirm', {
        extraction_id: confirmData.extraction_id,
        entity_type: entityType,
        approved_data: confirmData.approved_data,
        corrections: confirmData.corrections,
      });
      
      setApprovedData(confirmData.approved_data);
      setMode(INTAKE_MODES.COMPLETE);
      
      // Call the callback to populate the parent form
      if (onDataExtracted) {
        onDataExtracted(confirmData.approved_data);
      }
      
      toast.success('Data approved! Form has been populated.');
      
      // If in modal mode and onClose provided, close after brief delay
      if (isModal && onClose) {
        setTimeout(() => onClose(), 500);
      }
    } catch (err) {
      console.error('Confirmation error:', err);
      toast.error('Failed to confirm data. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  }, [entityType, onDataExtracted, isModal, onClose]);
  
  // Reset to start over
  const handleReset = useCallback(() => {
    setMode(INTAKE_MODES.TOOLBAR);
    setExtractionResult(null);
    setApprovedData(null);
  }, []);
  
  // Cancel and close
  const handleCancel = useCallback(() => {
    if (mode === INTAKE_MODES.REVIEWING) {
      // Go back to toolbar
      setMode(INTAKE_MODES.TOOLBAR);
      setExtractionResult(null);
    } else if (onClose) {
      onClose();
    }
  }, [mode, onClose]);
  
  // Render content based on mode
  const renderContent = () => {
    switch (mode) {
      case INTAKE_MODES.TOOLBAR:
        return (
          <div className="space-y-4">
            {showTitle && !compact && (
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{config.icon}</span>
                <div>
                  <h3 className="font-semibold">AI-Assisted {config.label} Entry</h3>
                  <p className="text-sm text-muted-foreground">{config.description}</p>
                </div>
              </div>
            )}
            
            <UniversalAIToolbar
              entityType={entityType}
              onExtract={handleExtract}
              isLoading={isExtracting}
              placeholder={`Describe the ${config.label.toLowerCase()}... Type naturally, paste content, upload a document, or speak.`}
            />
            
            {!compact && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-4">
                <Sparkles className="h-3 w-3" />
                <span>AI will extract structured data. You review and approve before saving.</span>
              </div>
            )}
          </div>
        );
      
      case INTAKE_MODES.REVIEWING:
        return (
          <AIReviewMode
            extractionResult={extractionResult}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            isLoading={isConfirming}
            entityType={entityType}
            existingData={existingData}
          />
        );
      
      case INTAKE_MODES.COMPLETE:
        return (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Data Extracted Successfully</h3>
            <p className="text-muted-foreground mb-4">
              {Object.keys(approvedData || {}).length} fields have been populated in the form.
            </p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={handleReset}>
                <Wand2 className="h-4 w-4 mr-2" />
                Extract More
              </Button>
              {onClose && (
                <Button onClick={onClose}>
                  Continue to Form
                </Button>
              )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  // Render as modal or inline
  if (isModal) {
    return (
      <Dialog open={true} onOpenChange={(open) => !open && onClose?.()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Universal Intake — {config.label}
            </DialogTitle>
            <DialogDescription>
              Enter information using any method. AI will extract and organize the data for you.
            </DialogDescription>
          </DialogHeader>
          {renderContent()}
        </DialogContent>
      </Dialog>
    );
  }
  
  // Inline card mode
  return (
    <Card className={cn("", className)} data-testid="universal-intake-form">
      {showTitle && (
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            AI-Assisted Entry
          </CardTitle>
          <CardDescription>
            Type, speak, upload, or paste — AI extracts the data
          </CardDescription>
        </CardHeader>
      )}
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
}

/**
 * Universal Intake Button
 * 
 * A simple button that opens the Universal Intake modal.
 * Use this when you want to add AI intake to an existing form.
 */
export function UniversalIntakeButton({
  entityType,
  onDataExtracted,
  existingData = null,
  variant = 'outline',
  size = 'default',
  className,
  children,
}) {
  const [isOpen, setIsOpen] = useState(false);
  
  const config = ENTITY_CONFIG[entityType] || { label: entityType, icon: '📄' };
  
  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setIsOpen(true)}
        className={className}
        data-testid={`ai-intake-btn-${entityType}`}
      >
        {children || (
          <>
            <Wand2 className="h-4 w-4 mr-2" />
            AI Assist
          </>
        )}
      </Button>
      
      {isOpen && (
        <UniversalIntakeForm
          entityType={entityType}
          onDataExtracted={(data) => {
            onDataExtracted(data);
            setIsOpen(false);
          }}
          existingData={existingData}
          isModal={true}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Universal Intake Panel
 * 
 * A collapsible panel that can be added to any form.
 * Expands to show AI intake options, collapses to save space.
 */
export function UniversalIntakePanel({
  entityType,
  onDataExtracted,
  existingData = null,
  defaultExpanded = false,
  className,
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  const config = ENTITY_CONFIG[entityType] || { label: entityType, icon: '📄' };
  
  return (
    <Card className={cn("overflow-hidden", className)}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
        data-testid={`ai-intake-panel-toggle-${entityType}`}
      >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Wand2 className="h-4 w-4 text-primary" />
          </div>
          <div className="text-left">
            <p className="font-medium text-sm">AI-Assisted Entry</p>
            <p className="text-xs text-muted-foreground">
              Type, speak, upload, or paste to auto-fill the form
            </p>
          </div>
        </div>
        <ChevronRight className={cn(
          "h-5 w-5 text-muted-foreground transition-transform",
          isExpanded && "rotate-90"
        )} />
      </button>
      
      {isExpanded && (
        <CardContent className="border-t pt-4">
          <UniversalIntakeForm
            entityType={entityType}
            onDataExtracted={onDataExtracted}
            existingData={existingData}
            showTitle={false}
            compact={true}
          />
        </CardContent>
      )}
    </Card>
  );
}

export default UniversalIntakeForm;
